/*
 * rheincool — Heizkosten-Rechner (Heizen-Artikel-Handoff.md)
 * Wärmebedarf(kWh/Saison) = Fläche × W/m² ÷ 1000 × Heizstunden/Saison.
 * Klima  = Bedarf ÷ COP × Strompreis · Bestand = Bedarf ÷ Wirkungsgrad ×
 * Energiepreis (Gas/Öl aus #rc-constants, Strom-Direkt = Strompreis).
 * Ausgabe in €/Monat (Saison ÷ heizsaisonMonate) + Ersparnis €/Saison.
 * Store-Key 'heating' ({eurKlima, eurBestand, ersparnisSaison}).
 */
(() => {
  'use strict';
  if (customElements.get('rc-heating-cost-calculator')) return;

  class RcHeatingCostCalculator extends window.RcCalculator {
    init() {
      this.storeKey = 'heating';
      this.trackPrefix = 'heating_calc';
      this.lastSignature = null;
      this.fuelDirty = false;

      this.els = {
        clima: this.querySelector('[data-heating-clima]'),
        existing: this.querySelector('[data-heating-existing]'),
        existingLabel: this.querySelector('[data-heating-existing-label]'),
        save: this.querySelector('[data-heating-save]'),
        areaLabel: this.querySelector('[data-calc-label-value="area"]'),
        fuelGroup: this.querySelector('[data-heating-fuel-group]'),
        fuelLabel: this.querySelector('[data-heating-fuel-label]'),
        fuelField: this.querySelector('[data-calc-field="fuel_price"]'),
        powerField: this.querySelector('[data-calc-field="power_price"]'),
        stickyValue: this.querySelector('[data-heating-sticky-value]'),
        tableClimaMonth: this.querySelector('[data-heating-table-clima-month]'),
        tableClimaSeason: this.querySelector('[data-heating-table-clima-season]'),
        tableExistingLabel: this.querySelector('[data-heating-table-existing-label]'),
        tableExistingMonth: this.querySelector('[data-heating-table-existing-month]'),
        tableExistingSeason: this.querySelector('[data-heating-table-existing-season]'),
        ctas: Array.from(this.querySelectorAll('[data-heating-cta]')),
      };

      /* Preise aus den zentralen Konstanten vorbefüllen */
      if (this.els.powerField && this.els.powerField.value === '') {
        this.els.powerField.value = this.constantPrice('energyPriceCtKwh', 35);
      }
      if (this.els.fuelField) {
        if (this.els.fuelField.value === '') {
          this.els.fuelField.value = this.constantPrice('gasPriceCtKwh', 12);
        }
        this.els.fuelField.addEventListener('input', () => {
          this.fuelDirty = true;
          this.update();
        });
      }
      if (this.els.powerField) {
        this.els.powerField.addEventListener('input', () => this.update());
      }

      /* Heizungs-Wechsel: Gas-/Öl-Feld umbeschriften + neu vorbefüllen */
      this.querySelectorAll('[data-calc-segments="system"] [data-calc-segment]').forEach((segment) => {
        segment.addEventListener('click', () => this.syncFuelField(segment.dataset.value));
      });

      this.els.ctas.forEach((cta) => {
        cta.addEventListener('click', () => {
          const result = window.rcStore?.get('heating');
          this.track('heating_cta_click', {
            ersparnisSaison: result ? Math.round(result.ersparnisSaison) : null,
          });
        });
      });
    }

    fill(template, values) {
      return Object.keys(values).reduce(
        (text, key) => text.split(`[${key}]`).join(values[key]),
        template || ''
      );
    }

    constantPrice(key, fallback) {
      return typeof this.constants[key] === 'number' ? this.constants[key] : fallback;
    }

    syncFuelField(system) {
      const s = this.config.strings || {};
      if (!this.els.fuelGroup) return;
      if (system === 'strom') {
        this.els.fuelGroup.hidden = true;
        return;
      }
      this.els.fuelGroup.hidden = false;
      if (this.els.fuelLabel) {
        this.els.fuelLabel.textContent = system === 'oel' ? s.labelFuelOil : s.labelFuelGas;
      }
      if (!this.fuelDirty && this.els.fuelField) {
        this.els.fuelField.value =
          system === 'oel'
            ? this.constantPrice('oilPriceCtKwh', 11)
            : this.constantPrice('gasPriceCtKwh', 12);
      }
    }

    fuelPrice(system, powerPrice) {
      if (system === 'strom') return powerPrice;
      const price = this.fieldValue('fuel_price');
      if (!Number.isNaN(price) && price > 0) return price;
      return system === 'oel'
        ? this.constantPrice('oilPriceCtKwh', 11)
        : this.constantPrice('gasPriceCtKwh', 12);
    }

    compute() {
      const area = this.fieldValue('area');
      if (Number.isNaN(area) || area <= 0) return null;
      const c = this.config;
      const system = this.segmentValue('system') || 'gas';

      let powerPrice = this.fieldValue('power_price');
      if (Number.isNaN(powerPrice) || powerPrice <= 0) {
        powerPrice = this.constantPrice('energyPriceCtKwh', 35);
      }
      const energyPrice = this.fuelPrice(system, powerPrice);
      const efficiency = { gas: c.effGas, oel: c.effOil, strom: c.effElectric }[system] || 1;

      const bedarfKwh = (area * c.wpm2) / 1000 * c.heatingHours;
      const klimaSaison = (bedarfKwh / c.cop) * (powerPrice / 100);
      const bestandSaison = (bedarfKwh / efficiency) * (energyPrice / 100);
      const monate = typeof this.constants.heizsaisonMonate === 'number' ? this.constants.heizsaisonMonate : 6;

      return {
        eurKlima: klimaSaison / monate,
        eurBestand: bestandSaison / monate,
        eurKlimaSaison: klimaSaison,
        eurBestandSaison: bestandSaison,
        ersparnisSaison: bestandSaison - klimaSaison,
        flaeche: area,
        heizung: system,
        strompreis: powerPrice,
        energiepreis: energyPrice,
      };
    }

    renderResult(result) {
      const s = this.config.strings || {};
      const systemLabel = {
        gas: s.systemGas,
        oel: s.systemOil,
        strom: s.systemElectric,
      }[result.heizung];
      const climaMonth = this.formatEuro(result.eurKlima, 0);
      const existingMonth = this.formatEuro(result.eurBestand, 0);
      const saveText = this.formatNumber(Math.max(0, result.ersparnisSaison), 0);

      if (this.els.areaLabel) this.els.areaLabel.textContent = `${this.formatNumber(result.flaeche, 0)} m²`;
      if (this.els.clima) this.els.clima.textContent = climaMonth;
      if (this.els.existing) this.els.existing.textContent = existingMonth;
      if (this.els.existingLabel) this.els.existingLabel.textContent = systemLabel;
      if (this.els.tableExistingLabel) this.els.tableExistingLabel.textContent = systemLabel;
      if (this.els.tableClimaMonth) this.els.tableClimaMonth.textContent = climaMonth;
      if (this.els.tableClimaSeason) this.els.tableClimaSeason.textContent = this.formatEuro(result.eurKlimaSaison, 0);
      if (this.els.tableExistingMonth) this.els.tableExistingMonth.textContent = existingMonth;
      if (this.els.tableExistingSeason) this.els.tableExistingSeason.textContent = this.formatEuro(result.eurBestandSaison, 0);

      const hasSaving = result.ersparnisSaison > 0;
      if (this.els.save) {
        this.els.save.classList.toggle('is-neutral', !hasSaving);
        this.els.save.textContent = hasSaving ? this.fill(s.saveLine, { x: saveText }) : s.noSave;
      }
      if (this.els.stickyValue) {
        this.els.stickyValue.textContent = hasSaving
          ? this.fill(s.stickySave, { x: saveText })
          : climaMonth;
      }
      this.announce(
        this.fill(s.announce, { klima: climaMonth, bestand: existingMonth })
      );

      const signature = `${Math.round(result.eurKlima)}|${Math.round(result.eurBestand)}`;
      if (signature !== this.lastSignature) {
        this.lastSignature = signature;
        this.track('heating_calc_result', {
          ersparnisSaison: Math.round(result.ersparnisSaison),
          flaeche: result.flaeche,
          heizung: result.heizung,
        });
      }
    }

    trackParams() {
      return {
        flaeche: this.fieldValue('area'),
        heizung: this.segmentValue('system') || 'gas',
        strompreis: this.fieldValue('power_price'),
        energiepreis: this.fieldValue('fuel_price'),
      };
    }
  }

  customElements.define('rc-heating-cost-calculator', RcHeatingCostCalculator);
})();
